/* verqo — Buyer Pricing page (preview build) */
import { ArrowRight, CheckCircle2, Star } from "lucide-react";
import { SubPageLayout, Reveal, Eyebrow, CTABanner, FAQ } from "../../../components/marketing/SubPageBlocks";
import PageSEO from "../../../components/marketing/PageSEO";

const PLANS = [
  {
    id: "free", name: "Free SaaS", sub: "No software fee for buyers",
    price: "$0", priceSub: "/ month SaaS fee",
    heading: "What's included",
    bullets: [
      "Order from your existing suppliers",
      "One cart, many suppliers",
      "Pay by ACH, card, wire, or line",
      "Buyer protection on every parcel",
    ],
    cta: "Request invite",
  },
  {
    id: "financing", name: "Pay-as-you-go financing", sub: "For extended terms & split-pay",
    price: "1-3%", priceSub: "per invoice extended",
    eyebrow: "Optional", featured: true,
    heading: "Included when you extend",
    bullets: [
      "Net-30/60/90 across every supplier",
      "Extend up to 120 days",
      "Split any invoice into 3 or 6",
      "Transparent rate before you commit",
    ],
    cta: "See financing",
  },
  {
    id: "enterprise", name: "Enterprise", sub: "For chains & procurement teams",
    price: "Custom",
    heading: "Everything, plus",
    bullets: [
      "Location-level P&L",
      "Approval workflows + SSO",
      "Punch-out catalogs (Coupa, Ariba)",
      "Dedicated CSM",
      "Custom credit limits",
    ],
    cta: "Contact us",
  },
];

/* Usage-based fees — buyers pay only for the services they use */
const USAGE_FEES = [
  ["ACH transfer (standard)", "Free", "T+1 settlement from your Verqo wallet"],
  ["Same-day ACH", "0.5%", "Capped at $15 per transfer"],
  ["Domestic wire", "$15", "T+0 settlement"],
  ["Card payment", "2.9% + 30¢", "Pay any invoice by credit or debit card"],
  ["Extended-terms financing", "1–3%", "Per invoice extended — priced before you commit"],
  ["Shipping labels & freight", "Pay as you go", "Discounted carrier rates — parcel & LTL/FTL"],
  ["Supplier discovery", "Paid add-on", "Finding NEW suppliers on the network — pricing announced at launch"],
];

const FAQ_ITEMS = [
  { q: "Is Verqo free for buyers?", a: "The software is — buyers never pay a monthly SaaS fee. You do pay usage-based fees for the services you choose to use: banking transfers (wires, same-day ACH, cards), shipping labels, extended-terms financing, and supplier discovery. If you only order from your existing suppliers and pay by standard ACH, you pay nothing." },
  { q: "What does supplier discovery cost?", a: "Ordering from suppliers you already work with is free. Discovering and connecting with NEW suppliers on the Verqo network is a paid add-on — pricing will be announced at launch." },
  { q: "What does financing cost?", a: "1-3% of the extended invoice — priced transparently before you commit. No origination fees, no prepayment penalties." },
  { q: "Does using financing hurt my credit?", a: "No. Verqo doesn't report to consumer bureaus. Your on-time payments build a portable Trade Score used only across the Verqo network." },
];

export default function BuyerPricingPreview() {
  return (
    <SubPageLayout modeOverride="buyer">
      <PageSEO
        title="Buyer pricing · verqo"
        description="No SaaS fee for buyers — pay only for the services you use: banking transfers, shipping, extended-terms financing, and supplier discovery."
        path="/buyers/pricing"
      />
      <section className="relative pt-20 md:pt-28 pb-8 px-6 md:px-10 max-w-[1280px] mx-auto text-center" data-testid="buyer-pricing-hero">
        <Reveal><Eyebrow>Pricing · Buyers</Eyebrow></Reveal>
        <Reveal delay={0.08}>
          <h1 className="mt-6 text-[36px] md:text-[64px] leading-[1.02] tracking-tight font-semibold text-slate-900">
            No SaaS fee. <span className="text-slate-400 italic">Pay for what you use.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-6 max-w-2xl mx-auto text-[15px] md:text-[16px] leading-relaxed text-slate-600">
            Buyers never pay a monthly software fee. You pay only for the services you choose to use —
            <strong className="text-slate-900"> banking transfers, shipping, extended-terms financing, and supplier discovery.</strong>
          </p>
        </Reveal>
      </section>

      <section className="relative py-10 px-6 md:px-10 max-w-[1300px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {PLANS.map((t, i) => (
            <Reveal key={t.id} delay={i * 0.06}>
              <div
                data-testid={`buyer-pricing-card-${t.id}`}
                className={`h-full rounded-2xl p-7 md:p-8 flex flex-col ${
                  t.featured ? "border-2 border-[#0c1612] bg-white shadow-[0_24px_64px_-24px_rgba(132,204,22,0.35)] -translate-y-2"
                              : "border border-slate-200 bg-white"
                }`}
              >
                {t.eyebrow && (
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-semibold w-fit mb-4 bg-lime-300 text-slate-900">
                    <Star className="w-3 h-3" /> {t.eyebrow}
                  </div>
                )}
                <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">{t.name}</div>
                <div className="text-[12.5px] text-slate-500 mt-1 leading-relaxed">{t.sub}</div>
                <div className="mt-6 flex items-baseline gap-1.5">
                  <span className="text-[44px] font-semibold tracking-tight text-slate-900 leading-none">{t.price}</span>
                  {t.priceSub && <span className="text-[13px] text-slate-500">{t.priceSub}</span>}
                </div>
                <a
                  href="/waitlist?as=buyer"
                  data-testid={`buyer-pricing-cta-${t.id}`}
                  className={`mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg text-sm font-semibold transition-colors ${
                    t.featured ? "bg-[#0c1612] text-white hover:opacity-90"
                               : "bg-white border border-slate-300 text-slate-900 hover:bg-slate-50"
                  }`}
                >
                  {t.cta} <ArrowRight className="w-3.5 h-3.5" />
                </a>
                <div className="mt-7 pt-6 border-t border-slate-200">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-4">{t.heading}</div>
                  <ul className="space-y-2.5 flex-1">
                    {t.bullets.map((b, bi) => (
                      <li key={bi} className="flex items-start gap-2 text-[13px] text-slate-700 leading-relaxed">
                        <CheckCircle2 className="w-3.5 h-3.5 text-lime-600 mt-0.5 flex-shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Usage-based fee schedule — banking, shipping, financing, discovery */}
      <section className="relative py-12 px-6 md:px-10 max-w-[900px] mx-auto" data-testid="buyer-fee-table">
        <Reveal>
          <div className="text-center mb-8">
            <Eyebrow>Usage-based fees</Eyebrow>
            <h2 className="mt-4 text-[26px] md:text-[36px] font-semibold tracking-tight text-slate-900">Banking &amp; service fees</h2>
            <p className="mt-3 text-[13.5px] text-slate-500 max-w-xl mx-auto">The software is free — these are the only fees buyers ever pay, and only when you use the service.</p>
          </div>
        </Reveal>
        <Reveal delay={0.08}>
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            {USAGE_FEES.map(([svc, fee, note], i) => (
              <div key={svc} className={`flex items-center justify-between gap-4 px-6 py-4 ${i > 0 ? "border-t border-slate-100" : ""}`} data-testid={`buyer-fee-row-${i}`}>
                <div className="min-w-0">
                  <div className="text-[14px] font-semibold text-slate-900">{svc}</div>
                  <div className="text-[12px] text-slate-500 mt-0.5">{note}</div>
                </div>
                <div className="text-[14px] font-semibold text-slate-900 whitespace-nowrap tabular-nums">{fee}</div>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      <FAQ items={FAQ_ITEMS} />

      <CTABanner
        title="Ready to unlock terms across every supplier?"
        subtitle="Verqo Buyer is invite-only during launch. Join the list — we'll get you set up in under 48 hours."
        primary={{ label: "Request invite", href: "/waitlist?as=buyer" }}
        secondary={{ label: "Talk to us", href: "/company/contact" }}
      />
    </SubPageLayout>
  );
}
